import { chunk, compact, get, isEmpty, isEqual, uniq } from "lodash"
import {
  AvailableDisplayId,
  AvailableDaysOfOperation,
  ProviderLicense,
  LiabilityInsurance,
} from "../../domain/Provider/schema"
import { DataStatus } from "../../domain/enums"
import {
  CACHE_KEY_CASE_ID,
  CACHE_KEY_REFERRAL_PACKET,
} from "../../domain/Account/userSliceCacheKeys"
import { Provider } from "../../domain/Provider/schema"
import { Practice } from "../../domain/Practice/schema"
import * as practicesSchema from "../../domain/Practices/schema"
import {
  LocationPhotoTypeEnum,
  PublicLocationPhoto,
} from "../../domain/Profile/schema"
import { ALLOWED_DOCUMENT_TYPES, ALLOWED_IMAGE_TYPES } from "./file"
import { isValidDate } from "./components/FlowbiteDatepicker/helpers"

export enum LiabilityStatusEnum {
  ACTIVE = "active",
  EXPIRED = "expired",
  NOT_AVAILABLE = "not-available",
  EXPIRING_SOON = "expiring-soon",
  PENDING = "pending",
  REQUIRED = "required",
  NOT_APPLICABLE = "not-applicable",
}

export enum LicenseStatusEnum {
  ACTIVE = "active",
  EXPIRED = "expired",
  PENDING = "pending",
  EXPIRING_SOON = "expiring-soon",
  NOT_AVAILABLE = "not-available",
}

const {
  LOADING: LOADING_STATUS,
  SENDING: SENDING_STATUS,
  PENDING: PENDING_STATUS,
  UPDATING: UPDATING_STATUS,
  LOADED: LOADED_STATUS,
  ERROR: ERROR_STATUS,
  SAVED: SAVED_STATUS,
} = DataStatus

export type DeltaTuple = [any, any]

/**
 * Compares two arrays for equality, regardless of element order.
 *
 * @param {any[]} array1 - The first array to compare.
 * @param {any[]} array2 - The second array to compare.
 * @returns {boolean} - Returns true if the arrays contain the same elements (regardless of order), false otherwise.
 */
const arraysAreEqual = (array1: any[], array2: any[]): boolean => {
  if (array1.length !== array2.length) return false
  const sortedArray1 = [...array1].sort()
  const sortedArray2 = [...array2].sort()
  for (let i = 0; i < sortedArray1.length; i++) {
    if (!isEqual(sortedArray1[i], sortedArray2[i])) return false
  }
  return true
}

/**
 * Checks if there is a difference between the values of the tuples. The values can be of any type, including arrays.
 *
 * @param {DeltaTuple[]} tuples - An array of tuples to compare. Each tuple is a pair of values to compare.
 * @returns {boolean} - Returns true if there is a difference between the values of the tuples, false otherwise.
 */
export const tupleArrayDelta = (tuples: DeltaTuple[]): boolean => {
  for (const [value1, value2] of tuples) {
    if (Array.isArray(value1) && Array.isArray(value2)) {
      if (!arraysAreEqual(value1, value2)) return true
    } else if (!isEqual(value1, value2)) {
      return true
    }
  }
  return false
}

/**
 * Checks if there is a difference between the values of the tuples.
 * @deprecated Use tupleArrayDelta instead.
 *
 * @param {DeltaTuple[]} tuples - An array of tuples to compare. Each tuple is a pair of values to compare.
 * @returns {boolean} - Returns true if there is a difference between the values of the tuples, false otherwise.
 */
export const reduxStateReactStateDelta = (tuples: DeltaTuple[]): boolean => {
  for (const [value1, value2] of tuples) {
    if (value1 !== value2) {
      return true
    }
  }
  return false
}

/**
 * Forces a UTC interpretation of the given datetime string if it is not already UTC.
 * This is necessary because backend services assume UTC, but do not encode timezone in their datestrings.
 *
 * @param {string} utcDateTime DateTime as string.
 * @returns {Date} utcDateTime as a UTC initialized Date object.
 */
export const getDateFromAssumedUtcDateTime = (utcDateTime: string): Date => {
  const isDateUTC = utcDateTime.toLowerCase().endsWith("z")
  const hasTimestamp = utcDateTime.toLowerCase().includes("t")
  return !isDateUTC && hasTimestamp
    ? new Date(utcDateTime + "Z")
    : new Date(utcDateTime)
}

/**
 * Converts a utcDateTime string from the API into a human-friendly
 * date string in local time.
 *
 * @param utcDateTime DateTime string representing UTC timestamp.
 * @returns A friendly string in local time (MM/DD/YYYY).
 */
export const utcDateTimeToLocalDateString = (utcDateTime: string): string => {
  // Make sure the incoming string is interpreted
  // by the Date constructor as UTC by appending(+ "Z") if it doesnt already exist.
  let date: Date
  if (utcDateTime.endsWith("Z")) {
    date = new Date(utcDateTime)
  } else {
    date = new Date(utcDateTime + "Z")
  }

  const month = date.getMonth() + 1 // getMonth() returns month from 0-11
  const day = date.getDate()
  const year = date.getFullYear()

  // Pad single digits with leading zero
  const formattedMonth = month < 10 ? `0${month}` : month
  const formattedDay = day < 10 ? `0${day}` : day

  return `${formattedMonth}/${formattedDay}/${year}`
}

export const formatDateTime = (
  dateString: string | null | undefined,
  timeString?: string,
  placeholder = "--",
): string => {
  if (!dateString) return placeholder

  const utcString = timeString ? `${dateString}T${timeString}` : dateString

  // Handle regular datetime strings
  const date = getDateFromAssumedUtcDateTime(utcString)
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })
}

export const toIsoDateString = (dateString: string): string => {
  // Handles "MM/DD/YYYY" or "M/D/YYYY" and returns "YYYY-MM-DD"
  const [month, day, year] = dateString.split("/")
  if (year && month && day) {
    return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
  }
  // If already in ISO format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString
  return dateString // fallback
}

/**
 * Formats a raw digit string into a phone display format. By default, format
 * is xxx-yyy-zzzz or yyy-zzzz.
 * @param phoneNumber
 * @param useParens (optional) if true, format uses (xxx) yyy-zzzz format.
 * @returns
 */
export const formatPhoneNumber = (
  phoneNumber: string | null,
  useParens = false,
): string => {
  if (!phoneNumber) {
    return ""
  }
  const normalizedNumber = phoneNumber.replace(/\D/g, "")

  let formattedNumber
  // Format the number based on its length
  if (normalizedNumber.length <= 3) {
    formattedNumber = normalizedNumber
  } else if (normalizedNumber.length <= 7) {
    // Format as "XXX-XXXX" for up to 7 digits
    formattedNumber =
      normalizedNumber.slice(0, 3) + "-" + normalizedNumber.slice(3)
  } else {
    if (useParens) {
      formattedNumber = `(${normalizedNumber.slice(
        0,
        3,
      )}) ${normalizedNumber.slice(3, 6)}-${normalizedNumber.slice(6)}`
    } else {
      // Format as "XXX-XXX-XXXX" for more than 7 digits
      formattedNumber =
        normalizedNumber.slice(0, 3) +
        "-" +
        normalizedNumber.slice(3, 6) +
        "-" +
        normalizedNumber.slice(6)
    }
  }

  return formattedNumber
}

export const dataStatusesLoading = (dataStatusList: DataStatus[]): boolean => {
  return (
    dataStatusList.filter((status) =>
      [LOADING_STATUS, SENDING_STATUS].includes(status),
    ).length > 0
  )
}

export const dataStatusesPending = (dataStatusList: DataStatus[]): boolean => {
  return dataStatusList.some((status) =>
    [LOADING_STATUS, PENDING_STATUS, SENDING_STATUS, UPDATING_STATUS].includes(
      status,
    ),
  )
}

export const dataStatusesLoaded = (dataStatusList: DataStatus[]): boolean => {
  return dataStatusList.every((status) => status === LOADED_STATUS)
}

export const dataStatusesSaved = (dataStatusList: DataStatus[]): boolean => {
  return dataStatusList.every((status) => status === SAVED_STATUS)
}

export const dataStatusesSending = (dataStatusList: DataStatus[]): boolean => {
  return dataStatusList.filter((status) => status === SENDING_STATUS).length > 0
}

const ALLOWED_BLOB_MIME_TYPES = new Set([
  ...ALLOWED_DOCUMENT_TYPES,
  ...ALLOWED_IMAGE_TYPES,
])

export const base64ToBlob = (base64String: string): Blob => {
  const byteString = atob(base64String.split(",")[1])
  const mimeString = base64String.split(",")[0].split(":")[1].split(";")[0]
  if (!ALLOWED_BLOB_MIME_TYPES.has(mimeString)) {
    throw new Error(`Unsupported MIME type: ${mimeString}`)
  }
  const byteNumbers = new Array(byteString.length)
  for (let i = 0; i < byteString.length; i++) {
    byteNumbers[i] = byteString.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  return new Blob([byteArray], { type: mimeString })
}

export const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(blob)
    reader.onloadend = () => {
      resolve(reader.result as string)
    }
    reader.onerror = reject
  })
}

export const clearHandoffCaseLocalStorage = () => {
  localStorage.removeItem(CACHE_KEY_CASE_ID)
  localStorage.removeItem(CACHE_KEY_REFERRAL_PACKET)
}

export const dataStatusesError = (dataStatusList: DataStatus[]): boolean => {
  return dataStatusList.filter((status) => ERROR_STATUS === status).length > 0
}

export const getRawNumericString = (
  formatted: string | null | undefined,
): string => {
  return formatted?.replace(/\D/g, "") ?? ""
}

/**
 * Map the AvailableDisplayId enums from backend into the fixed size array of booleans
 * representing the days of the week that are available
 * @param displayIds - array of AvailableDisplayId enums representing available days
 * @returns AvailableDaysOfOperation - Array with length of 7 representing days of the week as booleans
 */
export const mapAvailableDays = (
  displayIds: AvailableDisplayId[],
): AvailableDaysOfOperation => {
  const availableDaysValues = Object.values(AvailableDisplayId).filter(
    (value) => typeof value === "string",
  ) as unknown as AvailableDisplayId[]

  const mapOfAvailableDays = availableDaysValues.map((day) =>
    displayIds.includes(day),
  )
  return mapOfAvailableDays as AvailableDaysOfOperation
}

export function getFilenameFromContentDisposition(contentDisposition: string) {
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
  const matches = filenameRegex.exec(contentDisposition)
  if (matches != null && matches[1]) {
    // Remove quotes if they are present
    let filename = matches[1].replace(/['"]/g, "")
    // Decode URI-encoded characters
    filename = decodeURIComponent(filename)
    // Sanitize to prevent path traversal and control character injection
    filename = filename
      .replace(/[/\\]/g, "_")
      .replace(/[\x00-\x1F]/g, "")
      .trim()
    return filename
  }
  // Fallback to a default filename if necessary
  return "defaultFilename.ext"
}

/**
 * Returns the number of days between two dates.
 * - counting how many midnights are crossed
 * e.g. given: 12/08/2025 and 06/12/2025, return 179
 * Positive number if date1 is after date2, negative if before. (ie. date1 - date2)
 */
export const diffCalendarSpan = (date1: Date, date2: Date) => {
  const diff = new Date(date1).getTime() - new Date(date2).getTime()
  const days = Math.ceil(diff / 86_400_000) // 86,400,000 ms per day
  return days
}

export const getLiabilityStatusDisplayId = (
  liabilityStatusDisplayId: string,
  issueDate?: Date | string | null,
  comparisonDate: Date = new Date(),
) => {
  if (!issueDate) {
    return liabilityStatusDisplayId
  }

  const parsedIssueDate = new Date(issueDate)
  if (Number.isNaN(parsedIssueDate.getTime())) {
    return liabilityStatusDisplayId
  }

  return diffCalendarSpan(parsedIssueDate, comparisonDate) > 0
    ? LiabilityStatusEnum.PENDING
    : liabilityStatusDisplayId
}

/**
 * Returns the number of days between two dates.
 * - ignoring DST anomalies
 * e.g. given: 12/08/2025 and 06/12/2025, return 180
 * Positive number if date1 is after date2, negative if before. (ie. date1 - date2)
 */
export const diffCalendarDays = (date1: Date, date2: Date) => {
  const d1 = Date.UTC(
    date1.getUTCFullYear(),
    date1.getUTCMonth(),
    date1.getUTCDate(),
  )
  const d2 = Date.UTC(
    date2.getUTCFullYear(),
    date2.getUTCMonth(),
    date2.getUTCDate(),
  )
  return Math.trunc((d1 - d2) / 86_400_000) // 86,400,000 ms per day
}

export const sortOptionsByLabel = (optionsMap: Record<string, string>) => {
  return Object.entries(optionsMap)
    .map(([typeId, displayId]) => {
      return { label: displayId, value: typeId }
    })
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const genOptionsChunks = (
  map: Record<string, string>,
  numOptionsPerChunk: number = 3,
  shouldSort: boolean = true,
) => {
  const options = Object.keys(map).map((key) => {
    return {
      value: key,
      label: map[key],
    }
  })

  if (shouldSort) {
    options.sort((a, b) => a.label.localeCompare(b.label))
  }

  return chunk(options, numOptionsPerChunk)
}

/**
 * Returns the next day's date.
 * @param date - The current date.
 * @returns A new Date object representing the next day.
 */
export const getNextDay = (date: Date): Date => {
  const nextDay = new Date(date)
  nextDay.setDate(date.getDate() + 1)
  return nextDay
}

export const getMinExpiresDate = (issuedDate: string) => {
  if (isValidDate(issuedDate) && new Date(issuedDate) > new Date()) {
    return getNextDay(new Date(issuedDate))
  } else {
    return getNextDay(new Date())
  }
}
/**
 * Returns a formatted date string in the shape of MM/DD/YYYY
 * @param language - The language to use for formatting the date. (ie. 'en')
 * @param date - The date to format.
 * @param options - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat#using_options
 */
export const getFormattedDate = (
  language: string,
  date: Date,
  options?: Intl.DateTimeFormatOptions,
): string => {
  let defaultOptions: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }

  if (options) {
    defaultOptions = options
  }
  return new Intl.DateTimeFormat(language, defaultOptions).format(date)
}

export const isDateBeforeToday = (date: Date) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return date < today
}

export const populateCmsTemplate = (
  data: string[],
  cmsKeyName: string,
  template?: string,
) => {
  if (!template || isEmpty(template)) {
    console.error(`CMS key ${cmsKeyName} is missing`)
    return ""
  }
  if (isEmpty(data)) {
    console.error(`Data is missing for CMS key ${cmsKeyName}`)
    return template
  }
  data.forEach((item, index) => {
    template = template?.replace(`{${index}}`, item)
  })

  return template
}

const getAggregate = <T>(
  objects: T[],
  path: keyof T,
  prescendence: string[],
): string => {
  const sorted = objects
    .filter((obj) => prescendence.includes(get(obj, path)))
    .sort(
      (a, b) =>
        prescendence.indexOf(get(a, path)) - prescendence.indexOf(get(b, path)),
    )
  return sorted.length === 0 ? "not-available" : get(sorted.at(0), path)
}

export const getAggregateLicenseStatus = (
  licenses: ProviderLicense[],
): LicenseStatusEnum => {
  return getAggregate(licenses, "licenseStatusDisplayId", [
    "expired",
    "expiring-soon",
    "active",
  ]) as LicenseStatusEnum
}

export const getAggregateInsuranceStatus = (
  insurances: LiabilityInsurance[],
): LiabilityStatusEnum => {
  return getAggregate(insurances, "liabilityInsuranceStatusDisplayId", [
    "active",
    "expiring-soon",
    "expired",
    "pending",
  ]) as LiabilityStatusEnum
}

/**
 * Downloads a file from a Blob object.
 * @param blob - The Blob object containing the file data.
 * @param fileName - The name of the file to be downloaded.
 * @returns void
 */
export const downloadFileBlob = (blob: Blob, fileName: string): void => {
  // sanitize filename to prevent any injected behaviors
  const safeFileName =
    fileName
      .replace(/[/\\]/g, "_")
      .replace(/\0/g, "")
      .replace(/[\x00-\x1F]/g, "")
      .trim() || "download"
  const downloadUrl = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = downloadUrl
  link.download = safeFileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(downloadUrl)
}

/**
 * Sorts two strings in alphanumeric order.
 */
export const sortAlphaNum = (a?: string, b?: string) => {
  if (!a || !b) {
    return 0
  }

  if (typeof a !== "string" || typeof b !== "string") {
    return 0
  }
  return a.localeCompare(b, "en", { numeric: true })
}

/**
 * Validates a U.S. zip code.
 * Matches 5-digit zip codes (e.g., 12345) or 9-digit zip codes with a hyphen (e.g., 12345-6789).
 * @param zipCode - The zip code to validate.
 * @returns True if the zip code is valid, false otherwise.
 */
export const validateZipCode = (zipCode: string): boolean => {
  const zipCodeRegex = /^\d{5}(-\d{4})?$/
  return zipCodeRegex.test(zipCode)
}

/**
 * Parses datetime strings literally without any timezone conversion.
 * Handles multiple formats:
 * - "YYYY-MM-DD HH:MM:SS.fff" (SQL Server format)
 * - "YYYY-MM-DDTHH:MM:SS" (ISO format without timezone)
 * - "YYYY-MM-DD" (date only)
 *
 * @param dateTimeStr - The datetime string to parse
 * @param options - Formatting options
 * @returns Object with formatted date and time strings
 */
export const parseDateTimeLiteral = (
  dateTimeStr: string | null | undefined,
  options: {
    dateFormat?: "MM/DD/YYYY" | "YYYY-MM-DD"
    timeFormat?: "12hour" | "24hour"
    placeholder?: string
  } = {},
): { date: string; time: string; dateTime: string } => {
  const {
    dateFormat = "MM/DD/YYYY",
    timeFormat = "12hour",
    placeholder = "--",
  } = options

  if (!dateTimeStr) {
    return { date: placeholder, time: placeholder, dateTime: placeholder }
  }

  // Handle different separators (space or T)
  const [datePart, timePart] = dateTimeStr.includes("T")
    ? dateTimeStr.split("T")
    : dateTimeStr.split(" ")

  let formattedDate = placeholder
  let formattedTime = placeholder

  // Parse date part: "YYYY-MM-DD"
  if (datePart) {
    const [year, month, day] = datePart.split("-")
    if (year && month && day) {
      formattedDate =
        dateFormat === "MM/DD/YYYY"
          ? `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`
          : `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
    }
  }

  // Parse time part: "HH:MM:SS.fff" or "HH:MM:SS"
  if (timePart) {
    const timeOnly = timePart.split(".")[0] // Remove microseconds if present
    const [hours, minutes] = timeOnly.split(":")

    if (hours && minutes) {
      const hour24 = parseInt(hours, 10)

      if (timeFormat === "12hour") {
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
        const ampm = hour24 >= 12 ? "PM" : "AM"
        formattedTime = `${hour12}:${minutes} ${ampm}`
      } else {
        formattedTime = `${hours}:${minutes}`
      }
    }
  }

  const dateTime =
    formattedDate !== placeholder && formattedTime !== placeholder
      ? `${formattedDate} ${formattedTime}`
      : formattedDate !== placeholder
        ? formattedDate
        : placeholder

  return { date: formattedDate, time: formattedTime, dateTime }
}

/**
 * Convenience function for parsing session date and time separately.
 * Useful when date and time are stored in separate fields.
 */
export const parseSessionDateTime = (
  sessionDate: string | undefined,
  sessionTime: string | undefined,
  options: Parameters<typeof parseDateTimeLiteral>[1] = {},
): { date: string; time: string } => {
  const { placeholder = "--" } = options

  // Handle session date (usually in MM/DD/YYYY format)
  const formattedDate = sessionDate || placeholder

  // Handle session time (usually in HH:MM:SS format)
  let formattedTime = placeholder
  if (sessionTime) {
    const [hours, minutes] = sessionTime.split(":")
    if (hours && minutes) {
      const hour24 = parseInt(hours, 10)
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
      const ampm = hour24 >= 12 ? "PM" : "AM"
      formattedTime = `${hour12}:${minutes} ${ampm}`
    }
  }

  return { date: formattedDate, time: formattedTime }
}

/**
 * Formats user input into a date string in MM/DD/YYYY format as they type.
 * Automatically inserts slashes and restricts input to numbers valid slashes.
 *
 * @param input - The raw input string from the user.
 * @returns A formatted date string in MM/DD/YYYY format.
 */
export const formatDateInput = (input: string) => {
  // Remove all non-numeric characters except slashes
  const cleaned = input.replace(/[^\d/]/g, "")

  let formatted = ""
  let digitCount = 0

  for (let i = 0; i < cleaned.length; i++) {
    const char = cleaned[i]

    if (char === "/") {
      // Only allow slash after 2 digits (month) or 5 characters (month/day)
      if (formatted.length === 2 || formatted.length === 5) {
        formatted += char
      }
    } else {
      formatted += char
      digitCount++

      // Auto-add slash after month (2 digits) or day (4 digits total)
      if (digitCount === 2 || digitCount === 4) {
        if (!formatted.endsWith("/") && i < cleaned.length - 1) {
          formatted += "/"
        }
      }
    }
  }

  return formatted
}

const convertBySchema = (
  items: practicesSchema.LiabilityInsurance[],
): LiabilityInsurance[] => {
  return items
    ? items.map((x) => ({
        liabilityInsuranceStatusDisplayId: x.insuranceStatus,
      }))
    : []
}

const getGroupLiabilityStatus = (
  practice: Practice | practicesSchema.FullPractice,
): { group: boolean; status: LiabilityStatusEnum } => {
  const uploads = isEmpty(practice.liabilityInsurances)
    ? []
    : convertBySchema(
        practice.liabilityInsurances as practicesSchema.LiabilityInsurance[],
      )
  return isEmpty(uploads)
    ? {
        group: true,
        status: LiabilityStatusEnum.REQUIRED,
      }
    : { group: true, status: getAggregateInsuranceStatus(uploads) }
}

const getProviderLiabilityUploads = (
  providers: Provider[],
): LiabilityStatusEnum[] => {
  return isEmpty(providers)
    ? []
    : uniq(
        compact(
          providers
            .filter((p) => !isEmpty(p.liabilityInsurances))
            .map((p) => getAggregateInsuranceStatus(p.liabilityInsurances)),
        ),
      )
}

const determineProviderLiabilityStatus = (
  uploads: LiabilityStatusEnum[],
  statusPriority: LiabilityStatusEnum[],
): LiabilityStatusEnum => {
  if (uploads.every((x) => x === LiabilityStatusEnum.ACTIVE))
    return LiabilityStatusEnum.ACTIVE

  for (const status of statusPriority) {
    if (uploads.some((x) => x === status)) return status
  }

  return LiabilityStatusEnum.NOT_AVAILABLE
}

export const liabilityStatusNotification = (
  practice: Practice | practicesSchema.FullPractice,
  providers: Provider[],
): { group: boolean; status: LiabilityStatusEnum } => {
  // use Practice, if "Group Insurance" is provided
  if (practice.authReleaseRequired) {
    return getGroupLiabilityStatus(practice)
  }

  const uploads = getProviderLiabilityUploads(providers)

  if (isEmpty(uploads))
    return {
      group: false,
      status: LiabilityStatusEnum.REQUIRED,
    }

  const statusPriority = [
    LiabilityStatusEnum.EXPIRED,
    LiabilityStatusEnum.EXPIRING_SOON,
    LiabilityStatusEnum.PENDING,
  ]

  return {
    group: false,
    status: determineProviderLiabilityStatus(uploads, statusPriority),
  }
}

export const liabilityStatusOverall = (
  practice: Practice | practicesSchema.FullPractice,
  providers: Provider[],
): { group: boolean; status: LiabilityStatusEnum } => {
  // use Practice, if "Group Insurance" is provided
  if (practice.authReleaseRequired) {
    return getGroupLiabilityStatus(practice)
  }

  // no Group Insurance, so ...
  // use Providers, as "Individual Insurance"
  const hasRequiredLiability = providers.some((p) =>
    isEmpty(p.liabilityInsurances),
  )

  if (hasRequiredLiability)
    return { group: false, status: LiabilityStatusEnum.REQUIRED }

  const uploads = getProviderLiabilityUploads(providers)

  if (isEmpty(uploads))
    return {
      group: false,
      status: LiabilityStatusEnum.REQUIRED,
    }

  const statusPriority = [
    LiabilityStatusEnum.EXPIRED,
    LiabilityStatusEnum.PENDING,
    LiabilityStatusEnum.EXPIRING_SOON,
  ]

  return {
    group: false,
    status: determineProviderLiabilityStatus(uploads, statusPriority),
  }
}

export const optimisticSortOrder = {
  active: 0,
  "expiring-soon": 1,
  pending: 2,
  expired: 3,
}

export const sortProviderLiabilityInsurances = (
  liabilityInsurances: LiabilityInsurance[],
  statusOrder: typeof optimisticSortOrder = optimisticSortOrder,
): LiabilityInsurance[] => {
  if (isEmpty(liabilityInsurances)) return []
  return liabilityInsurances.sort(
    (a, b) =>
      statusOrder[
        a.liabilityInsuranceStatusDisplayId as keyof typeof optimisticSortOrder
      ] -
      statusOrder[
        b.liabilityInsuranceStatusDisplayId as keyof typeof optimisticSortOrder
      ],
  )
}

/**
 * Sort profile photos by type.
 * This ensures that "General" photos appear first, followed by "Animal", then "Home", and finally any other types.
 * @param {PublicLocationPhoto} photo
 * @returns {number} - A negative number if the first photo should come before the second, a positive number if after, or zero if they are equal.
 */
export const sortProfilePhotos = (photo: PublicLocationPhoto) => {
  if (photo.locationPhotoTypeDisplayId === LocationPhotoTypeEnum.General) {
    return -1
  }
  if (photo.locationPhotoTypeDisplayId === LocationPhotoTypeEnum.Animal) {
    return 1
  }
  if (photo.locationPhotoTypeDisplayId === LocationPhotoTypeEnum.Home) {
    return 2
  }
  return 0
}
